import { addDays, compareAsc, isSameDay, max } from "date-fns";
import { uniqBy } from "lodash";
import { fromDatabaseFormat, toDatabaseFormat } from "./dates";

export type DateRange = { from: string; to: string };
export type DateRangeDate = { from: Date; to: Date };

const mergeRangesGeneric = <T extends { from: unknown; to: unknown }>(
  ranges: T[],
  dateConverter: (value: unknown) => Date,
  dateBuilder: (from: Date, to: Date) => T
): T[] => {
  if (ranges.length === 0) return [];

  const sortedRanges = [...ranges].sort((a, b) =>
    compareAsc(dateConverter(a.from), dateConverter(b.from))
  );

  const merged = sortedRanges.reduce<T[]>(
    (acc, range) => {
      if (acc.length === 0) return [range];

      const last = acc[acc.length - 1];
      const lastEnd = dateConverter(last.to);
      const currentStart = dateConverter(range.from);
      const dayAfterLastEnd = addDays(lastEnd, 1);

      if (
        (currentStart < dayAfterLastEnd || isSameDay(currentStart, dayAfterLastEnd))
      ) {
        const mergedEnd = max([
          dateConverter(last.to),
          dateConverter(range.to),
        ]);
        const merged = dateBuilder(dateConverter(last.from), mergedEnd);
        acc[acc.length - 1] = merged;
        return acc;
      }

      return [...acc, range];
    },
    []
  );

  return uniqBy(merged, (r) => `${r.from}-${r.to}`);
};

export const mergeDateRanges = (ranges: DateRange[]): DateRange[] =>
  mergeRangesGeneric(
    ranges,
    (value) => fromDatabaseFormat(value as string),
    (from, to) => ({
      from: toDatabaseFormat(from),
      to: toDatabaseFormat(to),
    })
  );

export const mergeDateRangesDate = (ranges: DateRangeDate[]): DateRangeDate[] =>
  mergeRangesGeneric(
    ranges,
    (value) => value as Date,
    (from, to) => ({
      from,
      to,
    })
  );
